/**
 * Inline Suggestions Block Renderer (formerly Pills)
 * Renders inline CTA "suggestion pills" using @strivelabs/ui-blocks Suggestions components
 */

import React from "react";
import type { UIBlock, UIActionEvent } from "@/types/ui-blocks";
import { Suggestions as UISuggestions } from "@strivelabs/ui-blocks";
import { Button } from "@strivelabs/ui-core";

export interface InlineSuggestionsBlockRendererProps {
  block: UIBlock;
  onUIAction: (event: UIActionEvent) => void;
  /** When true, the component is disabled */
  readOnly?: boolean;
}

// Map color variants to Tailwind border classes for semantic styling
const colorVariantMap: Record<string, string> = {
  red: "border border-red-200 text-red-600 hover:bg-red-100 hover:text-red-700 hover:border-red-300",
  green: "border border-green-200 text-green-600 hover:bg-green-100 hover:text-green-700 hover:border-green-300",
  yellow: "border border-yellow-200 text-yellow-600 hover:bg-yellow-100 hover:text-yellow-700 hover:border-yellow-300",
  gray: "border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-700 hover:border-gray-300",
};

export function InlineSuggestionsBlockRenderer({
  block,
  onUIAction,
  readOnly = false
}: InlineSuggestionsBlockRendererProps) {
  if (block.type !== "inline_suggestions") return null;

  // Don't render inline suggestions in read-only mode
  if (readOnly) return null;

  const { content } = block;

  const handleClick = (item: { label: string; value: string; shouldRedirect?: boolean }) => {
    onUIAction({
      type: "ui_action",
      component: "inline_suggestions",
      label: content.label,
      state: { 
        action: { value: item.value, label: item.label },
        shouldRedirect: item.shouldRedirect ?? false
      },
    });
  };

  return (
    <div className="flex flex-col gap-2">
      {content.label ? <div className="text-sm font-medium text-muted-foreground">{content.label}</div> : null}
      <UISuggestions className="justify-start">
        {content.items.map((item, idx) => {
          const colorClass = item.colorVariant ? colorVariantMap[item.colorVariant] : "";
          return (
            <Button
              key={`${item.value}-${idx}`}
              onClick={() => handleClick(item)}
              variant={item.variant ?? "outline"}
              size={item.size ?? "sm"}
              disabled={item.disabled}
              className={`cursor-pointer rounded-full px-4 transition-colors ${
                colorClass || "text-custom-text-primary border-custom-border"
              }`}
            >
              {item.label}
            </Button>
          );
        })}
      </UISuggestions>
    </div>
  );
}

/**
 * Backward compatibility: alias for old pills naming
 * @deprecated Use InlineSuggestionsBlockRenderer instead
 */
export const PillsBlockRenderer = InlineSuggestionsBlockRenderer;
